"use client";

import { useState } from "react";
import Link from "@/components/Link";
import {
  Image,
  Box as MantineBox,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { blogFormatDate } from "@/lib/shared/blogUtils";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import styles from "./styles.module.scss";

const INITIAL_COUNT = 10;
const LOAD_MORE_COUNT = 9;

function formatCategory(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} className={styles.card}>
      {post.featured_image && (
        <MantineBox className={styles.cardImage}>
          <Image src={post.featured_image} alt={post.title} fit="contain" w="100%" loading="lazy" />
        </MantineBox>
      )}
      <MantineBox className={styles.cardBody}>
        <MantineText className={styles.cardTitle}>{post.title}</MantineText>
        {post.excerpt && <MantineText className={styles.cardExcerpt}>{post.excerpt}</MantineText>}
        <MantineBox className={styles.tagRow}>
          {post.category && (
            <MantineText span className={styles.categoryTag}>
              {formatCategory(post.category)}
            </MantineText>
          )}
          {post.tags.map((t) => (
            <MantineText span key={t} className={styles.tag}>
              {t}
            </MantineText>
          ))}
        </MantineBox>
        <MantineText className={styles.cardMeta}>
          {post.author && <MantineText span>{post.author} · </MantineText>}
          {blogFormatDate(post.published_at)}
        </MantineText>
      </MantineBox>
    </Link>
  );
}

export default function BlogPostGrid({ posts }: { posts: BlogPost[] }) {
  const [active, setActive] = useState<string | null>(null);
  const [displayedCount, setDisplayedCount] = useState(INITIAL_COUNT);
  const [loadingMore, setLoadingMore] = useState(false);

  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean) as string[]));

  const visible = active ? posts.filter((p) => p.category === active) : posts;
  const displayed = visible.slice(0, displayedCount);
  const hasMore = displayedCount < visible.length;

  const handleCategoryClick = (category: string | null) => {
    setActive(category);
    // Switching categories changes what "visible" even is, so restart the
    // reveal count rather than keep whatever was scrolled to before.
    setDisplayedCount(INITIAL_COUNT);
  };

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setDisplayedCount((count) => Math.min(count + LOAD_MORE_COUNT, visible.length));
    setLoadingMore(false);
  };

  useInfiniteScroll(loadMore, hasMore, loadingMore, 0.8);

  return (
    <MantineBox>
      {categories.length > 0 && (
        <MantineBox className={styles.filterRow}>
          <MantineUnstyledButton
            className={`${styles.filterPill} ${active === null ? styles.filterPillActive : ""}`}
            onClick={() => handleCategoryClick(null)}
          >
            All
          </MantineUnstyledButton>
          {categories.map((cat) => (
            <MantineUnstyledButton
              key={cat}
              className={`${styles.filterPill} ${active === cat ? styles.filterPillActive : ""}`}
              onClick={() => handleCategoryClick(cat)}
            >
              {formatCategory(cat)}
            </MantineUnstyledButton>
          ))}
        </MantineBox>
      )}

      <MantineBox className={styles.grid}>
        {displayed.map((post) => (
          <PostCard key={post.slug} post={post} />
        ))}
      </MantineBox>
    </MantineBox>
  );
}
